/**
 * CoachMarks — a tiny, dependency-free guided-tour renderer.
 *
 * Anchors each step to a live `[data-tour="..."]` element: dims the rest of
 * the screen with a spotlight cutout around the target, and shows a popover
 * (desktop) or a bottom sheet (phones — the field canvasser's device) with
 * the step copy plus Back / Skip / Next controls.
 *
 * Deliberately hand-rolled rather than pulling in driver.js/joyride: the
 * behaviour we need (spotlight + a positioned card + a mobile sheet + Esc to
 * skip) is small, and a bespoke component keeps us self-contained and on the
 * design-system tokens. The one genuinely fiddly part — anchor positioning —
 * is handled by re-measuring on scroll/resize and for a few frames after each
 * step (so smooth-scroll settling doesn't leave the card stranded).
 *
 * The host decides WHEN a tour runs and what "done" means (mark seen). This
 * component only renders an active tour and calls `onFinish` when the user
 * completes the last step or skips.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { Tour, TourPlacement } from './tours';

type Rect = { top: number; left: number; width: number; height: number };

const CARD_W = 320;
const GAP = 12; // space between target and card
const MARGIN = 8; // viewport clamp margin

function resolve(target: string): HTMLElement | null {
  try {
    return document.querySelector(target) as HTMLElement | null;
  } catch {
    return null;
  }
}

/** Card position for desktop, clamped to the viewport. */
function placeCard(rect: Rect, placement: TourPlacement, cardH: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top: number;
  let left: number;
  switch (placement) {
    case 'top':
      top = rect.top - cardH - GAP;
      left = rect.left + rect.width / 2 - CARD_W / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - cardH / 2;
      left = rect.left - CARD_W - GAP;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - cardH / 2;
      left = rect.left + rect.width + GAP;
      break;
    case 'bottom':
    default:
      top = rect.top + rect.height + GAP;
      left = rect.left + rect.width / 2 - CARD_W / 2;
      break;
  }
  // Clamp inside the viewport.
  left = Math.max(MARGIN, Math.min(left, vw - CARD_W - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - cardH - MARGIN));
  return { top, left };
}

/** How long to wait for a step's anchor to mount before giving up on it. */
const ANCHOR_GRACE_MS = 3000;
const ANCHOR_POLL_MS = 100;
/**
 * How often, while a step is showing, we check that its anchor is still on
 * screen. A spotlight the user can't see is just a dimmed screen, so we keep
 * the anchor in view for the step's whole lifetime rather than only at its
 * start — late layout shifts and the browser's own scroll restoration
 * (history.scrollRestoration === 'auto' fires well after mount on a reload)
 * both move the page out from under us.
 */
const SETTLE_TICK_MS = 150;

/**
 * Only correct when the anchor is COMPLETELY off screen. If it's even
 * partially visible we leave the page alone, so a user nudging the scroll
 * mid-step isn't fought for control.
 */
function isFullyOutOfView(r: DOMRect): boolean {
  return r.bottom <= 0 || r.top >= window.innerHeight;
}

export function CoachMarks({
  tour,
  onFinish,
  onAbort,
}: {
  tour: Tour;
  /** User completed or skipped the tour — the host should mark it seen. */
  onFinish: () => void;
  /**
   * The tour could not run: its anchors never mounted (e.g. the view was still
   * loading, or the user navigated away). The host should hide it WITHOUT
   * marking it seen, so it re-offers next time. Optional — defaults to
   * onFinish's hide-only behaviour is NOT assumed; if omitted we simply stop.
   */
  onAbort?: () => void;
}) {
  const isMobile = useIsMobile();
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  /** Did we ever successfully display a step? Drives finish-vs-abort. */
  const shownAnyRef = useRef(false);

  const step = tour.steps[stepIndex];
  const isLast = stepIndex === tour.steps.length - 1;

  const finish = useCallback(() => onFinish(), [onFinish]);
  const abort = useCallback(() => {
    if (onAbort) onAbort();
    else onFinish();
  }, [onAbort, onFinish]);
  const next = useCallback(() => {
    if (isLast) finish();
    else setStepIndex((i) => i + 1);
  }, [isLast, finish]);
  const back = useCallback(() => setStepIndex((i) => Math.max(0, i - 1)), []);

  const stepIndexRef = useRef(stepIndex);
  const rectRef = useRef<Rect | null>(null);
  // Hosts pass inline arrows for onFinish/onAbort, so `finish`/`abort` change
  // identity on every render. Held in refs and kept OUT of the step effect's
  // deps — otherwise that effect (and its scrollIntoView) would re-run on every
  // render, yanking the page back to the anchor continuously.
  const finishRef = useRef(finish);
  const abortRef = useRef(abort);
  useEffect(() => {
    finishRef.current = finish;
    abortRef.current = abort;
  }, [finish, abort]);

  // Re-measure the CURRENT step's anchor and update `rect` only when it
  // actually moved (avoids re-render thrash). Reads the step via ref so the
  // scroll/resize listeners never close over a stale step. If the anchor is
  // absent, leave the last rect in place — the step-change effect handles a
  // genuinely missing target.
  const measure = useCallback(() => {
    const s = tour.steps[stepIndexRef.current];
    const el = s ? resolve(s.target) : null;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const prev = rectRef.current;
    if (
      !prev ||
      prev.top !== r.top ||
      prev.left !== r.left ||
      prev.width !== r.width ||
      prev.height !== r.height
    ) {
      const nr = { top: r.top, left: r.left, width: r.width, height: r.height };
      rectRef.current = nr;
      shownAnyRef.current = true;
      setRect(nr);
    }
  }, [tour]);

  // On step change: sync the ref, scroll the target into view, and measure
  // SYNCHRONOUSLY. useLayoutEffect fires on every commit regardless of tab
  // visibility — critical, because requestAnimationFrame is throttled/paused
  // in hidden/background tabs, so an rAF-only measure would leave the
  // spotlight stranded. A couple of deferred re-measures catch smooth-scroll
  // settling when the tab *is* visible. If the target is genuinely absent,
  // don't strand the user: advance, or finish on the last step.
  useLayoutEffect(() => {
    stepIndexRef.current = stepIndex;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let waited = 0;

    // Poll for the anchor rather than declaring it missing on the first miss:
    // a view that's still loading (skeleton) mounts its real elements a beat
    // later, and treating that as "absent" would silently burn through every
    // step and mark the tour seen without ever showing it.
    // Keep the anchor on screen for as long as this step is showing. Runs for
    // the step's lifetime (not a fixed window) because the things that move the
    // page — async content growing above the anchor, the browser restoring a
    // scroll position on reload — land at unpredictable times, often seconds in.
    const keepAnchorVisible = (el: HTMLElement) => {
      if (cancelled) return;
      if (isFullyOutOfView(el.getBoundingClientRect())) {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
      measure();
      timer = setTimeout(() => keepAnchorVisible(el), SETTLE_TICK_MS);
    };

    const attempt = () => {
      if (cancelled) return;
      const s = tour.steps[stepIndex];
      const el = s ? resolve(s.target) : null;
      if (el) {
        // Instant, not smooth: a smooth animation races the settle checks below
        // (the anchor reads as out-of-view mid-flight) and fights them.
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        measure();
        keepAnchorVisible(el);
        return;
      }
      waited += ANCHOR_POLL_MS;
      if (waited < ANCHOR_GRACE_MS) {
        timer = setTimeout(attempt, ANCHOR_POLL_MS);
        return;
      }
      // Anchor genuinely never showed up.
      if (stepIndex < tour.steps.length - 1) {
        setStepIndex((i) => i + 1); // skip this step, try the next
      } else if (shownAnyRef.current) {
        finishRef.current(); // they saw earlier steps — count it as done
      } else {
        abortRef.current(); // nothing rendered — re-offer, don't mark seen
      }
    };
    attempt();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [stepIndex, tour, measure]);

  // Keep the anchor glued to the target as the page scrolls or resizes.
  useEffect(() => {
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [measure]);

  // Esc skips the whole tour.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [finish]);

  // Render nothing until the current step's anchor has been measured. This is
  // what makes the grace-period wait invisible: while a view is still loading
  // its skeleton, the tour simply isn't there yet — no dim, no stray card —
  // and it appears the moment the real element mounts.
  if (!step || !rect || typeof document === 'undefined') return null;

  const counter = `${stepIndex + 1} of ${tour.steps.length}`;

  // --- shared card body -----------------------------------------------------
  const cardBody = (
    <div ref={cardRef} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wide text-accent-fg">
          Step {counter}
        </span>
        <button
          type="button"
          onClick={finish}
          className="text-xs text-fg-muted hover:text-fg-primary font-medium"
        >
          Skip tour
        </button>
      </div>
      <div>
        <h3 className="text-base font-bold text-fg-primary">{step.title}</h3>
        <p className="mt-1 text-sm text-fg-secondary leading-snug">{step.body}</p>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        {/* progress dots */}
        <div className="flex items-center gap-1.5">
          {tour.steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIndex ? 'w-4 bg-accent' : 'w-1.5 bg-border-subtle'
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={back}
              className="text-sm font-semibold text-fg-secondary hover:text-fg-primary px-3 py-2 min-h-11 sm:min-h-0"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={next}
            className="bg-accent hover:bg-accent-hover text-white text-sm font-bold px-4 py-2 rounded-lg min-h-11 sm:min-h-0"
          >
            {isLast ? 'Done' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );

  const overlay = (
    <div className="fixed inset-0 z-[9500]" aria-live="polite" role="dialog" aria-modal="true">
      {/* Spotlight: a box over the target whose massive box-shadow dims
          everything else, leaving the target lit. pointer-events-none so the
          user can still see (not interact with) the highlighted control. */}
      {/* Dim everything except the anchor, using four viewport-bounded panels.
          The usual trick is one element with `box-shadow: 0 0 0 9999px`, but
          that ~20,000px shadow layer mis-composites on a long page — on the
          County Command Center it painted as a giant slab offset from the
          content, leaving an apparently blank screen. Four panels stay within
          the viewport and composite cleanly. */}
      {(() => {
        const t = rect.top - 6;
        const l = rect.left - 6;
        const w = rect.width + 12;
        const h = rect.height + 12;
        const dim = 'absolute bg-black/55';
        return (
          <>
            <div className={dim} style={{ top: 0, left: 0, right: 0, height: Math.max(0, t) }} />
            <div className={dim} style={{ top: Math.max(0, t + h), left: 0, right: 0, bottom: 0 }} />
            <div
              className={dim}
              style={{ top: Math.max(0, t), left: 0, width: Math.max(0, l), height: Math.max(0, h) }}
            />
            <div
              className={dim}
              style={{ top: Math.max(0, t), left: l + w, right: 0, height: Math.max(0, h) }}
            />
          </>
        );
      })()}

      {/* The spotlight ring itself — no fill, so the anchor shows through. */}
      <div
        className="pointer-events-none absolute rounded-lg ring-2 ring-accent transition-all duration-150"
        style={{
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
        }}
      />

      {isMobile ? (
        <div className="absolute inset-x-0 bottom-0 bg-bg-base border-t border-border-base rounded-t-2xl shadow-ds-xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {cardBody}
        </div>
      ) : (
        <div
          className="absolute bg-bg-base border border-border-base rounded-xl shadow-ds-xl p-4"
          style={{
            width: CARD_W,
            ...placeCard(rect, step.placement ?? 'bottom', cardRef.current?.offsetHeight ?? 160),
          }}
        >
          {cardBody}
        </div>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}

export default CoachMarks;
